import React from 'react';
import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { Authenticator, ThemeProvider, Theme, View, Heading, Text } from '@aws-amplify/ui-react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { darkTheme } from './theme';
import { AppRouter } from './routes';
import ErrorBoundary from './components/ErrorBoundary';
import outputs from '../amplify_outputs.json';
import './index.css';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@aws-amplify/ui-react/styles.css';

// Configure Amplify - Auth only, with all fields the Authenticator expects
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: outputs.auth.user_pool_id,
      userPoolClientId: outputs.auth.user_pool_client_id,
      identityPoolId: outputs.auth.identity_pool_id,
      loginWith: { username: true, email: true, phone: false },
      // MFA is controlled by the shared Cognito user pool config, not here.
      // Do not set mfa client-side — it misrepresents the pool's actual setting.
      passwordFormat: {
        minLength: 8,
        requireLowercase: true,
        requireUppercase: true,
        requireNumbers: true,
        requireSpecialCharacters: true,
      },
      userAttributes: { email: { required: true } },
    },
  },
});

// Log auth events for observability
Hub.listen('auth', ({ payload }) => {
  const ts = new Date().toISOString();
  switch (payload.event) {
    case 'signedIn':
      console.info(`[auth] ${ts} User signed in`);
      break;
    case 'signedOut':
      console.info(`[auth] ${ts} User signed out`);
      break;
    case 'tokenRefresh':
      console.info(`[auth] ${ts} Token refreshed`);
      break;
    case 'tokenRefresh_failure':
      console.warn(`[auth] ${ts} Token refresh failed`);
      break;
  }
});

const amplifyTheme: Theme = {
  name: 'nocland-dark',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#0d4f5c',
          20: '#0d4f5c',
          40: '#1a7a8a',
          60: '#00bcd4',
          80: '#4dd0e1',
          90: '#0097a7',
          100: '#0d4f5c',
        },
      },
      background: {
        primary: { value: '#1e293b' },
        secondary: { value: '#0f172a' },
      },
      font: {
        primary: { value: '#f1f5f9' },
        secondary: { value: '#94a3b8' },
      },
    },
  },
};

const components = {
  Header() {
    return (
      <View textAlign="center" padding="1.5rem">
        <Text fontSize="0.75rem" letterSpacing="0.15em" color="#00bcd4" fontWeight={600}>
          NOCLAND
        </Text>
        <Heading level={2} color="#f1f5f9" fontWeight={700}>
          Shift Summary
        </Heading>
        <Text color="#94a3b8" fontSize="0.875rem" paddingTop="0.5rem">
          Sign in to continue
        </Text>
      </View>
    );
  },
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MuiThemeProvider theme={darkTheme}>
      <CssBaseline />
      <ThemeProvider theme={amplifyTheme}>
        <Authenticator
          components={components}
          hideSignUp={true}
          loginMechanisms={['username']}
          variation="default"
          signUpAttributes={[]}
        >
          {() => (
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
          )}
        </Authenticator>
      </ThemeProvider>
    </MuiThemeProvider>
  </React.StrictMode>,
);
